import { PurchaseOrderStatus } from "@redotech/redo-prisma-client/enums";
import { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { SortDirection, TableSort } from "@redotech/redo-views-common/table";
import { DEFAULT_PAGE_SIZE, viewTypes } from "./views";

export enum PurchaseOrdersSystemView {
  ALL = "all",
  DRAFT = "draft",
  ORDERED = "ordered",
  CLOSED = "closed",
}

export const toTitleCase = (str: string) => {
  return str
    .trim()
    .replace(/\w\S*/g, (txt) =>
      txt.length > 2
        ? txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
        : txt,
    );
};

export const sortDefault: TableSort = {
  direction: SortDirection.DESC,
  key: "createdAt",
};

const getStatusFilter = (status: PurchaseOrderStatus) =>
  ({
    name: "status",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [status],
    operator: ArrayStringFilterOperator.ANY_OF,
  }) as AdvancedFilterData;

// These must match the column definitions found in the table, else "Save as
const defaultColumns = [
  { key: "poNumber", width: 150, hidden: false },
  { key: "shipments", width: 200, hidden: false },
  { key: "supplierName", width: 200, hidden: false },
  { key: "status", width: 150, hidden: false },
  { key: "reference", width: 150, hidden: true },
  { key: "tags", width: 200, hidden: false },
  { key: "receivedCount", width: 100, hidden: false },
  { key: "productCount", width: 100, hidden: false },
  { key: "expectedCount", width: 100, hidden: false },
  { key: "totalCost", width: 150, hidden: false },
  { key: "createdAt", width: 150, hidden: false },
];

export const StaticSystemViews: Record<
  PurchaseOrdersSystemView,
  viewTypes["purchase-orders"]
> = {
  [PurchaseOrdersSystemView.ALL]: {
    table: "purchase-orders",
    name: PurchaseOrdersSystemView.ALL,
    filters: [],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 0,
    isNotEditable: true,
  },
  [PurchaseOrdersSystemView.DRAFT]: {
    table: "purchase-orders",
    name: PurchaseOrdersSystemView.DRAFT,
    filters: [getStatusFilter(PurchaseOrderStatus.DRAFT)],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 0,
  },
  [PurchaseOrdersSystemView.ORDERED]: {
    table: "purchase-orders",
    name: PurchaseOrdersSystemView.ORDERED,
    filters: [getStatusFilter(PurchaseOrderStatus.ORDERED)],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 0,
  },
  [PurchaseOrdersSystemView.CLOSED]: {
    table: "purchase-orders",
    name: PurchaseOrdersSystemView.CLOSED,
    filters: [getStatusFilter(PurchaseOrderStatus.CLOSED)],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 0,
  },
};

export function friendlyViewName(view: viewTypes["purchase-orders"]) {
  switch (view.name) {
    case PurchaseOrdersSystemView.ALL:
      return "All";
    case PurchaseOrdersSystemView.DRAFT:
      return "Draft";
    case PurchaseOrdersSystemView.ORDERED:
      return "Ordered";
    case PurchaseOrdersSystemView.CLOSED:
      return "Closed";
    default:
      return view.name;
  }
}
